import Image, { ImageProps } from 'next/image'
import Link, { LinkProps } from 'next/link'
import { ComponentPropsWithoutRef } from 'react'

import { cn } from '../../../../_lib/utils'

const Sidebar = ({
  children,
  className,
}: ComponentPropsWithoutRef<'aside'>) => {
  return (
    <aside
      className={cn(
        'flex h-screen w-[200px] flex-col bg-brand-grayscale-1',
        className
      )}
    >
      {children}
    </aside>
  )
}

const SidebarHeader = ({
  children,
  className,
}: ComponentPropsWithoutRef<'header'>) => {
  return (
    <header className={cn('flex flex-col gap-4 px-8 py-6', className)}>
      {children}
    </header>
  )
}

const SidebarHeaderImage = ({ alt, ...props }: ImageProps) => {
  return <Image width={95} height={20} alt={alt} {...props} />
}

const SidebarHeaderText = ({
  children,
  className,
}: ComponentPropsWithoutRef<'p'>) => {
  return <p className={cn('text-xs', className)}>{children}</p>
}

const SidebarHeaderBrandText = ({
  children,
  className,
}: ComponentPropsWithoutRef<'span'>) => {
  return (
    <span
      className={cn('text-xs font-bold text-brand-green-primary-5', className)}
    >
      {children}
    </span>
  )
}

const SidebarItems = ({
  children,
  className,
}: ComponentPropsWithoutRef<'nav'>) => {
  return (
    <nav className={cn('flex flex-col gap-2 p-2', className)}>{children}</nav>
  )
}

const SidebarContent = ({
  children,
  className,
}: ComponentPropsWithoutRef<'div'>) => {
  return (
    <div className={cn('flex h-full flex-col justify-between', className)}>
      {children}
    </div>
  )
}

type SidebarButtonProps = {
  href: string
  isActive: boolean
  children: React.ReactNode
  className?: string
}

const SidebarButton = ({
  children,
  className,
  href,
  isActive,
}: LinkProps & SidebarButtonProps) => {
  const isActiveStyle =
    'border-brand-green-primary-5 bg-brand-green-primary-1 text-brand-green-primary-5'

  return (
    <Link
      href={href}
      className={cn(
        `flex items-center gap-2 rounded-[4px] border-l-4 border-brand-grayscale-1 px-6 py-3 transition-all ${
          isActive ? isActiveStyle : 'hover:bg-brand-grayscale-2'
        }`,
        className
      )}
    >
      {children}
    </Link>
  )
}

const SidebarButtonImage = ({ alt, ...props }: ImageProps) => {
  return <Image width={24} height={24} alt={alt} {...props} />
}

const SidebarButtonText = ({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<'span'>) => {
  return (
    <span className={cn('text-sm font-semibold', className)} {...props}>
      {children}
    </span>
  )
}

export {
  Sidebar,
  SidebarButton,
  SidebarButtonImage,
  SidebarButtonText,
  SidebarContent,
  SidebarHeader,
  SidebarHeaderBrandText,
  SidebarHeaderImage,
  SidebarHeaderText,
  SidebarItems,
}
